import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { StarIcon } from 'secondstep-components'
import GoalNotSetWarning from '../GoalNotSetWarning'
import { domains } from 'config/env'
import { isFlagOn } from 'utils/featureFlags'
import {
  DateText,
  EditButton,
  GoalCompletedText,
  GoalContentHeaderWrapper,
  GoalContentLeft,
  GoalContentWrapper,
  GoalMetricGroup,
  GoalStatusBadge,
  GoalSubTitle,
  GoalText,
  GoalTextLarge,
  GoalTitleWrapper,
  GoalUserManagementLink,
  GoalUserStatCount,
  GoalUserStatRow,
  GoalUserStatsDivider,
  GoalUserStatsPanel,
  StyledEditIcon,
  StyledGoalHr,
  StyledProgressBar,
} from '../component.styles'
import {
  BTS_2026_FLAG,
  EDIT_BUTTON_TEST_ID,
  MANAGE_USERS_LINK_TEXT,
  USER_MANAGEMENT_PATH,
  USERS_STAT_LABEL,
  USERS_WITHOUT_ACCOUNTS_STAT_LABEL,
  USERS_WITHOUT_CLASSES_STAT_LABEL,
} from '../constants'
import { formatShortDate, getClassesGoalStatus } from '../goalHelpers'

const formatUserStatValue = value => (value == null ? '-' : value)

const ClassesGoal = ({
  classCreationGoalValue,
  goalDate,
  isSingleSite,
  onNavigateToUserManagement,
  onOpenGoalsModal,
  onOpenGoalsModalFromWarning,
  showHeader,
  showMonitorViewOnly,
  siteId,
  sitePlaybookData,
  siteUserStats,
}) => {
  const classesCreated = sitePlaybookData?.classes_created || 0
  const classesCreatedPercentage = Math.round(
    (sitePlaybookData?.classes_created / classCreationGoalValue) * 100,
  )
  const isGoalSet = !!classCreationGoalValue && !!goalDate
  const goalStatusText = getClassesGoalStatus({
    goalDate,
    classesCreated: sitePlaybookData?.classes_created,
    classCreationGoalValue,
  })
  const { totalUsers, usersWithoutClasses, usersWithoutAccounts } =
    siteUserStats || {}
  const userManagementUrl = `${domains.ADMIN_DOMAIN}${USER_MANAGEMENT_PATH}/${siteId}`

  const userStatsPanel = (
    <>
      <GoalUserStatsDivider isSingleSite={isSingleSite} />
      <GoalUserStatsPanel data-testid="goal-user-stats-panel">
        <GoalUserStatRow>
          <GoalUserStatCount>
            {formatUserStatValue(totalUsers)}
          </GoalUserStatCount>
          <span>{USERS_STAT_LABEL}</span>
        </GoalUserStatRow>
        <GoalUserStatRow>
          <GoalUserStatCount>
            {formatUserStatValue(usersWithoutClasses)}
          </GoalUserStatCount>
          <span>{USERS_WITHOUT_CLASSES_STAT_LABEL}</span>
        </GoalUserStatRow>
        <GoalUserStatRow>
          <GoalUserStatCount>
            {formatUserStatValue(usersWithoutAccounts)}
          </GoalUserStatCount>
          <span>{USERS_WITHOUT_ACCOUNTS_STAT_LABEL}</span>
        </GoalUserStatRow>
        <GoalUserManagementLink
          data-testid="goal-manage-users-link"
          href={userManagementUrl}
          onClick={event => {
            event.preventDefault()
            onNavigateToUserManagement(userManagementUrl)
          }}
        >
          {MANAGE_USERS_LINK_TEXT}
        </GoalUserManagementLink>
      </GoalUserStatsPanel>
    </>
  )

  if (!isGoalSet) {
    return (
      <GoalContentWrapper
        hasUserStatsPanel={true}
        isSingleSite={isSingleSite}
        selectedGoalIsCompleted={false}
        showHeader={showHeader}
        showMonitorViewOnly={showMonitorViewOnly}
      >
        <GoalContentLeft>
          <GoalMetricGroup>
            <GoalTextLarge selectedGoalIsCompleted={false}>
              {classesCreated}
            </GoalTextLarge>
            <GoalText selectedGoalIsCompleted={false}>Classes Created</GoalText>
          </GoalMetricGroup>
          <GoalNotSetWarning onOpenGoalsModal={onOpenGoalsModalFromWarning} />
        </GoalContentLeft>
        {userStatsPanel}
      </GoalContentWrapper>
    )
  }

  return (
    <GoalContentWrapper
      hasUserStatsPanel={true}
      isSingleSite={isSingleSite}
      selectedGoalIsCompleted={true}
      showHeader={showHeader}
      showMonitorViewOnly={showMonitorViewOnly}
    >
      <GoalContentLeft>
        {goalStatusText !== null && (
          <GoalStatusBadge goalStatusText={goalStatusText}>
            {goalStatusText !== 'Goal Missed' && (
              <StarIcon id="goal-status-icon" />
            )}
            <span>{goalStatusText}</span>
          </GoalStatusBadge>
        )}
        <GoalContentHeaderWrapper>
          <DateText>Target Date: {formatShortDate(goalDate)}</DateText>
          {isFlagOn([BTS_2026_FLAG]) && (
            <EditButton
              data-testid={EDIT_BUTTON_TEST_ID}
              onClick={onOpenGoalsModal}
            >
              <StyledEditIcon aria-label="Edit Goals" />
            </EditButton>
          )}
        </GoalContentHeaderWrapper>
        <StyledGoalHr showMonitorViewOnly={showMonitorViewOnly} />
        <Fragment key="goal-content-completed">
          <GoalTitleWrapper>
            <GoalSubTitle>Classes Created</GoalSubTitle>
            <GoalCompletedText>
              {classesCreated} of {classCreationGoalValue} classes
            </GoalCompletedText>
          </GoalTitleWrapper>
          <StyledProgressBar
            goalValue={classCreationGoalValue}
            hideProgressText={true}
            percentComplete={classesCreatedPercentage}
            showGoalIndicator={true}
          />
        </Fragment>
      </GoalContentLeft>
      {userStatsPanel}
    </GoalContentWrapper>
  )
}

ClassesGoal.propTypes = {
  classCreationGoalValue: PropTypes.number,
  goalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSingleSite: PropTypes.bool,
  onNavigateToUserManagement: PropTypes.func,
  onOpenGoalsModal: PropTypes.func,
  onOpenGoalsModalFromWarning: PropTypes.func,
  showHeader: PropTypes.bool,
  showMonitorViewOnly: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sitePlaybookData: PropTypes.object,
  siteUserStats: PropTypes.shape({
    totalUsers: PropTypes.number,
    usersWithoutClasses: PropTypes.number,
    usersWithoutAccounts: PropTypes.number,
  }),
}

export default ClassesGoal
